import { createTheme, MantineColorsTuple, CSSVariablesResolver } from '@mantine/core';

const blue: MantineColorsTuple = [
  'var(--v2-color-blue-5)',
  'var(--v2-color-blue-10)',
  'var(--v2-color-blue-20)',
  'var(--v2-color-blue-30)',
  'var(--v2-color-blue-40)',
  'var(--v2-color-blue-50)',
  'var(--v2-color-blue-60)',
  'var(--v2-color-blue-70)',
  'var(--v2-color-blue-80)',
  'var(--v2-color-blue-85)',
];

const gray: MantineColorsTuple = [
  'var(--v2-color-neutral-1)',
  'var(--v2-color-neutral-5)',
  'var(--v2-color-neutral-10)',
  'var(--v2-color-neutral-15)',
  'var(--v2-color-neutral-20)',
  'var(--v2-color-warm-gray-25)',
  'var(--v2-color-warm-gray-45)',
  'var(--v2-color-neutral-60)',
  'var(--v2-color-warm-gray-75)',
  'var(--v2-color-neutral-90)',
];

const red: MantineColorsTuple = [
  'var(--v2-color-red-5)',
  'var(--v2-color-red-10)',
  'var(--v2-color-red-20)',
  'var(--v2-color-red-30)',
  'var(--v2-color-red-40)',
  'var(--v2-color-red-50)',
  'var(--v2-color-red-60)',
  'var(--v2-color-red-70)',
  'var(--v2-color-red-80)',
  'var(--v2-color-red-85)',
];

const green: MantineColorsTuple = [
  'var(--v2-color-apple-green-5)',
  'var(--v2-color-apple-green-10)',
  'var(--v2-color-apple-green-20)',
  'var(--v2-color-apple-green-30)',
  'var(--v2-color-apple-green-40)',
  'var(--v2-color-apple-green-50)',
  'var(--v2-color-apple-green-60)',
  'var(--v2-color-apple-green-70)',
  'var(--v2-color-apple-green-80)',
  'var(--v2-color-apple-green-85)',
];

const orange: MantineColorsTuple = [
  'var(--v2-color-orange-5)',
  'var(--v2-color-orange-10)',
  'var(--v2-color-orange-20)',
  'var(--v2-color-orange-30)',
  'var(--v2-color-orange-40)',
  'var(--v2-color-orange-50)',
  'var(--v2-color-orange-60)',
  'var(--v2-color-orange-70)',
  'var(--v2-color-orange-80)',
  'var(--v2-color-orange-85)',
];

export const adminThemeResolver: CSSVariablesResolver = () => ({
  variables: {
    '--mantine-color-body': 'var(--v2-color-neutral-5)',
    '--mantine-color-text': 'var(--xhr-color-content-neutral-strong)',
    '--mantine-color-dimmed': 'var(--xhr-color-content-neutral-light)',
    '--mantine-color-anchor': 'var(--xhr-color-content-primary)',
    '--mantine-color-error': 'var(--xhr-color-content-negative)',
  },
  light: {
    '--mantine-color-body': 'var(--v2-color-neutral-5)',
    '--mantine-color-text': 'var(--xhr-color-content-neutral-strong)',
    '--mantine-color-dimmed': 'var(--xhr-color-content-neutral-light)',
    '--mantine-color-anchor': 'var(--xhr-color-content-primary)',
    '--mantine-color-default': 'var(--xhr-color-bg-surface)',
    '--mantine-color-default-hover': 'var(--xhr-color-bg-fill-neutral-flat-hover)',
    '--mantine-color-default-color': 'var(--xhr-color-content-neutral-strong)',
    '--mantine-color-default-border': 'var(--xhr-color-border-neutral-light)',
    '--mantine-color-placeholder': 'var(--xhr-color-content-placeholder)',
    '--mantine-primary-color-filled': 'var(--xhr-color-bg-fill-primary)',
    '--mantine-primary-color-filled-hover': 'var(--xhr-color-bg-fill-primary-hover)',
    '--mantine-primary-color-light': 'var(--xhr-color-bg-fill-primary-subtle)',
    '--mantine-primary-color-light-hover': 'var(--xhr-color-bg-fill-primary-subtle-hover)',
    '--mantine-primary-color-light-color': 'var(--xhr-color-content-primary)',
    '--mantine-shadow-xs': 'var(--v2-elevation-lv1)',
    '--mantine-shadow-sm': 'var(--v2-elevation-lv1)',
    '--mantine-shadow-md': 'var(--v2-elevation-lv2)',
    '--mantine-shadow-lg': 'var(--v2-elevation-lv3)',
    '--mantine-shadow-xl': 'var(--v2-elevation-lv4)',
  },
  dark: {},
});

export const adminTheme = createTheme({
  primaryColor: 'blue',
  colors: {
    blue,
    gray,
    red,
    green,
    orange,
  },
  fontFamily:
    'var(--v2-font-family-sans), -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  headings: {
    fontFamily:
      'var(--v2-font-family-sans), -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: '600',
  },
  radius: {
    xs: 'var(--v2-border-radius-50)',
    sm: 'var(--v2-border-radius-100)',
    md: 'var(--v2-border-radius-200)',
    lg: 'var(--v2-border-radius-300)',
    xl: 'var(--v2-border-radius-400)',
  },
  spacing: {
    xs: 'var(--v2-spacing-100)',
    sm: 'var(--v2-spacing-200)',
    md: 'var(--v2-spacing-400)',
    lg: 'var(--v2-spacing-600)',
    xl: 'var(--v2-spacing-800)',
  },
  defaultRadius: 'md',
  shadows: {
    xs: 'var(--v2-elevation-lv1)',
    sm: 'var(--v2-elevation-lv1)',
    md: 'var(--v2-elevation-lv2)',
    lg: 'var(--v2-elevation-lv3)',
    xl: 'var(--v2-elevation-lv4)',
  },
  components: {
    Button: {
      defaultProps: {
        radius: 'md',
      },
    },
    Card: {
      defaultProps: {
        radius: 'lg',
        shadow: 'sm',
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'md',
      },
    },
    PasswordInput: {
      defaultProps: {
        radius: 'md',
      },
    },
  },
});
